import { distance } from '../core/math';
import { GULLET_JOB, LAST_ORDERS, ONE_ARMED_BANDITS, THE_COLLECTION, UNLOCK_MAGS_FIRM } from './ids';
import type { MissionScript } from './runner';
import { holdHint, leaveAMotor, swingKey, weaponKey } from './shared';
import type { MissionDef, RewardLine } from './types';

/**
 * The firm — the second story arc (jobs 4–6), unlocked by 'mags-firm' (The Gullet Job). After the
 * Gullet the Vanes push back, and it turns out DS Holloway has been on their books all along.
 * Everyone and everywhere here is original to Borough Heat:
 *
 *   Verger Pinch     St. Brannoc's; keeps a book in the vestry for Mags.
 *   Nobby Clegg      runs Mags' slate out of Lockup 7, Pilcher Ginnel.
 *   Sid Marlowe      the fence at Gorringe's scrap yard (docs/WEAPONS.md).
 *   Clive Vane       the quiet Vane brother; keeps the books — all of them.
 */

const health01 = (m: MissionScript, key: string): number => {
  const v = m.vehicle(key);
  return v ? Math.max(0, v.health / v.spec.maxHealth) : 0;
};
const raise = (m: MissionScript, level: number): void => {
  if (m.heat() < level) m.setHeat(level);
};

// ----------------------------------------------------------------------------- 4. One-Armed Bandits

/** The van counts as smashed at this condition (a bat does 7 a swing: about nine good ones). */
export const BANDITS_SMASHED = 0.55;
const BANDIT_VAN_PAINT = 0x5e1f2a;
/** Kerb outside The Gullet Arms (Basin Row); the van takes the nearest parking bay. */
const GULLET_ARMS_KERB = { x: 225, z: 123.5 };
export const NO_SHOOTERS = 'You fired a gun, and Mags said no guns. Now it’s a war — and you started it. Try again: bat or bumper only.';

export const oneArmedBandits: MissionDef = {
  id: ONE_ARMED_BANDITS,
  title: 'One-Armed Bandits',
  client: 'Mags Delaney',
  tag: 'JOB 04 · PERSUASION',
  blurb:
    'The Vanes are putting gambling machines into Mags’ pubs. Smash up their maroon delivery van outside The Gullet Arms with the cricket bat Mags gives you — no guns. Pays £1,500, more if you do it with the bat.',
  reward: 1500,
  requires: [GULLET_JOB],
  requiresUnlock: UNLOCK_MAGS_FIRM,
  giver: { poi: 'mission-start-anchor', prompt: 'Knock on Mags’ back-room door', glyph: 'M' },
  introFocus: { pos: GULLET_ARMS_KERB },
  intro: [
    { who: 'Mags', text: 'You’re one of mine now, so here’s how it works. The Vanes push, and we push back.' },
    { who: 'Mags', text: 'They’re putting one-armed bandits in every pub on the Basin. Their van’s outside The Gullet Arms.' },
    { who: 'Mags', text: 'Take the old bat from behind the bar. It’s a message, love, not a war. No shooters.' },
  ],
  setup: (m) => {
    m.spawnVehicle('bandits', 'van', { pos: GULLET_ARMS_KERB }, { locked: true, paint: BANDIT_VAN_PAINT, parked: true });
    if (m.giveWeapon('bat')) m.toast('Mags hands you a cricket bat.', 'good');
  },
  onAttempt: (m) => {
    // The one rule of the job, and the intro says it twice.
    m.listen('weaponFired', () => m.fail(NO_SHOOTERS), 'attempt');
  },
  stages: [
    {
      id: 'basin',
      kind: 'goTo',
      at: { vehicle: 'bandits' },
      radius: 16,
      text: 'Go to The Gullet Arms on Basin Row. The Vanes’ maroon van is parked outside.',
      hint: (touch) => `You’re holding Mags’ cricket bat. If you put it away, ${weaponKey(touch)} brings it back.`,
    },
    {
      id: 'smash',
      kind: 'custom',
      at: { vehicle: 'bandits' },
      radius: 4,
      text: 'Smash up the maroon van until the bar is full. Use the bat, or ram it with a car.',
      hint: (touch) => `Stand next to it, on foot, and swing (${swingKey(touch)}). Ramming works too, but it damages your car.`,
      done: (m) => {
        const v = m.vehicle('bandits');
        return !v || v.destroyed || health01(m, 'bandits') <= BANDITS_SMASHED;
      },
      progress: (m) => Math.min(1, (1 - health01(m, 'bandits')) / (1 - BANDITS_SMASHED)),
      onEnter: (m) => {
        m.listen('weaponHit', (e) => {
          if (e.target !== 'vehicle' || e.id !== m.vehicle('bandits')?.id) return;
          const hits = (typeof m.vars.batHits === 'number' ? m.vars.batHits : 0) + 1;
          m.vars.batHits = hits;
          if (hits === 1) m.say([{ who: 'Van driver', text: 'Oi! Leave it out! Them’s Mr Vane’s machines!' }]);
        });
      },
      onComplete: (m) => {
        m.say([
          { who: 'Landlord', text: 'Right, that’s it — I’m ringing the police! And Mr Vane!' },
          { who: 'Van driver', text: 'You’re a dead man. You hear me? Dead!' },
        ]);
        raise(m, 1);
        m.releaseVehicle('bandits');
      },
    },
    {
      id: 'scarper',
      kind: 'escapeHeat',
      text: 'The landlord called the police. Get away and lose them.',
      hint: 'On foot, use alleys and back yards — or take a car (press E next to one).',
      checkpoint: {
        label: 'the van’s smashed, police on the way',
        restore: (m) => {
          m.placePlayer({ pos: { x: GULLET_ARMS_KERB.x - 8, z: GULLET_ARMS_KERB.z } });
          // Nicked on foot last time? Something to drive stands at the kerb, so a retry on a phone isn't
          // a foot race against a patrol that's already on its way.
          leaveAMotor(m, { pos: { x: GULLET_ARMS_KERB.x - 14, z: GULLET_ARMS_KERB.z } });
          m.setHeat(1);
        },
      },
    },
    {
      id: 'ring',
      kind: 'goTo',
      at: { poi: 'phone-mercy' },
      radius: 3,
      mode: 'foot',
      requireHeat0: true,
      text: 'Call Mags from the phone box on Mercy Square.',
      blockedText: 'The police are still after you. Lose them first, then make the call.',
      hint: 'Get out of any car and walk into the phone box.',
    },
  ],
  outroFocus: { poi: 'phone-mercy' },
  outro: [
    { who: 'Mags (phone)', text: 'Heard it from here, love. Every landlord on the Basin knows what happens to Ronnie’s machines now.' },
    { who: 'Mags (phone)', text: 'Stay near that phone. Something’s up with my bookies, and I don’t like it.' },
  ],
  payout: (m) => {
    const lines: RewardLine[] = [{ label: 'Mags’ payment', amount: 1500 }];
    if (typeof m.vars.batHits === 'number' && m.vars.batHits >= 5) lines.push({ label: 'Done properly, with the bat', amount: 300 });
    return lines;
  },
};

// ----------------------------------------------------------------------------- 5. The Collection

const COLLECTION_TIME = 420;
/** A checkpoint retry carries the clock over (like Wet Paper), never below this. */
export const COLLECTION_RETRY_MIN = 150;
/** Time left at the back room for the bonus. */
export const COLLECTION_BONUS_LEFT = 150;
const RAIDED = 'Out of time: Holloway’s police raided Mags’ betting shops and took the money. Try again and be quicker.';

/** The collection clock for a checkpoint retry, given the time left when the last attempt failed. */
export function collectionRetryTime(leftAtFail: number | undefined): number {
  if (leftAtFail === undefined) return COLLECTION_TIME;
  return Math.min(COLLECTION_TIME, Math.max(COLLECTION_RETRY_MIN, leftAtFail));
}

const bag = (m: MissionScript, n: number): void => m.toast(`Bag ${n} of 3`, 'good');

export const theCollection: MissionDef = {
  id: THE_COLLECTION,
  title: 'The Collection',
  client: 'Mags Delaney',
  tag: 'JOB 05 · ROUNDS',
  blurb:
    'DS Holloway is raiding Mags’ betting shops (bookies) tonight. Within 7 minutes, collect three bags of money — from Verger Pinch at St. Brannoc’s Church, Nobby Clegg at Lockup 7 and Des Halloran at Halloran Motors — and bring them to Mags at The Crooked Anchor. Pays £1,800.',
  reward: 1800,
  requires: [ONE_ARMED_BANDITS],
  requiresUnlock: UNLOCK_MAGS_FIRM,
  giver: { poi: 'phone-mercy', prompt: 'Answer the ringing phone', glyph: '☎' },
  introFocus: { poi: 'st-brannocs' },
  intro: [
    { who: 'Mags (phone)', text: 'Holloway’s raiding my bookies tonight. Somebody’s paying him to, and I can guess who.' },
    { who: 'Mags (phone)', text: 'Three bags: the verger at St. Brannoc’s, Nobby at Lockup 7, and Des at Halloran’s.' },
    { who: 'Mags (phone)', text: 'They go in at eleven. That’s seven minutes, if you don’t stop for chips.' },
  ],
  setup: (m) => m.startTimer(COLLECTION_TIME, RAIDED),
  stages: [
    {
      id: 'verger',
      kind: 'hold',
      at: { poi: 'st-brannocs' },
      radius: 5,
      seconds: 2,
      prompt: 'Take the bag from the verger',
      text: 'Collect bag 1 from Verger Pinch at the door of St. Brannoc’s Church.',
      hint: holdHint('It’s just across Mercy Square. Stand next to him, on foot.'),
      onComplete: (m) => {
        bag(m, 1);
        m.say([{ who: 'Verger Pinch', text: 'Bless you. Mind, it’s the Lord’s money as much as Mags’.' }]);
      },
    },
    {
      id: 'slate',
      kind: 'hold',
      at: { poi: 'lockup-pilcher-7' },
      radius: 5,
      seconds: 2,
      prompt: 'Take the money bag from Nobby',
      text: 'Collect bag 2 from Nobby Clegg at Lockup 7, Pilcher Ginnel.',
      hint: holdHint('He’s at the lockup door, in Tanner’s Row. Stand next to him, on foot.'),
      onComplete: (m) => {
        bag(m, 2);
        m.say([{ who: 'Nobby Clegg', text: 'Tell Mags the three-thirty at Kempley was fixed. Not by me, mind.' }]);
      },
    },
    {
      id: 'yard',
      kind: 'hold',
      at: { poi: 'garage-halloran' },
      radius: 6,
      seconds: 2,
      prompt: 'Take the last bag off Des',
      text: 'Collect bag 3 from Des Halloran at Halloran Motors, Cinder Yards.',
      hint: holdHint('It’s across the borough — take a fast car. At the yard, get out.'),
      onComplete: (m) => {
        bag(m, 3);
        m.say([
          { who: 'Des Halloran', text: 'That’s the lot. Don’t look now, but CID’s been parked over the road an hour.' },
          { who: 'DS Holloway (radio)', text: 'He’s got the bags. Pull him — and no paperwork.' },
        ]);
        m.setHeat(1);
        m.callPolice({ poi: 'garage-halloran' }, { minDistance: 60 });
        // The bags are heavy and the clock is running: nobody should be left to run for it.
        leaveAMotor(m, { poi: 'garage-halloran', offset: 8 });
      },
    },
    {
      id: 'shake',
      kind: 'escapeHeat',
      text: 'Holloway’s police were watching the yard. Lose them before time runs out.',
      hint: 'Get out of their sight and stay hidden until your wanted level clears. Watch the timer.',
      checkpoint: {
        label: 'three bags in, police after you',
        restore: (m) => {
          m.spawnVehicle('getaway', 'compact', { poi: 'garage-halloran', offset: 8 });
          m.placePlayer({ vehicle: 'getaway' }, 'getaway');
          m.releaseVehicle('getaway');
          m.setHeat(1);
          const left = m.vars.timeLeftAtFail;
          m.startTimer(collectionRetryTime(typeof left === 'number' ? left : undefined), RAIDED);
        },
      },
    },
    {
      id: 'home',
      kind: 'goTo',
      at: { poi: 'mission-start-anchor' },
      radius: 6,
      requireHeat0: true,
      text: 'Bring the three bags to Mags’ back room at The Crooked Anchor.',
      blockedText: 'Don’t lead the police to Mags. Lose them first, then go in.',
    },
  ],
  outroFocus: { poi: 'pub-crooked-anchor' },
  outro: [
    { who: 'Mags', text: 'All three? Holloway’s lads kicked in three empty doors tonight.' },
    { who: 'Mags', text: 'Which tells me two things: he’s bent, and he’s getting desperate. Come back when you’ve had a drink.' },
  ],
  payout: (m) => {
    const lines: RewardLine[] = [{ label: 'Your cut of the takings', amount: 1800 }];
    const left = typeof m.vars.timeLeftAtEnd === 'number' ? m.vars.timeLeftAtEnd : 0;
    if (left > COLLECTION_BONUS_LEFT) lines.push({ label: 'Early — well before the raids', amount: 400 });
    return lines;
  },
};

// ----------------------------------------------------------------------------- 6. Last Orders

const CLIVE_PAINT = 0x17181c;
/** Hold time to jemmy the strongbox quietly; a shot within this many metres of the car opens it at once. */
export const STRONGBOX_SECONDS = 8;
export const STRONGBOX_SHOT_RANGE = 10;
/** Share of the quiet jemmy after which the doorman spots you (heat 1). */
export const DOORMAN_AT = 0.75;
const LEDGER_LOST = 'Clive’s black Regent was destroyed — and the ledger burned with it. Try again without wrecking the car.';

/** Put a revolver back in the player's hand if a busting took it (Sid keeps a spare). */
function rearm(m: MissionScript): void {
  const w = m.ctx.services.tryGet('weapons');
  if (w && !w.owned().includes('revolver')) m.giveWeapon('revolver', 12);
}

export const lastOrders: MissionDef = {
  id: LAST_ORDERS,
  title: 'Last Orders',
  client: 'Mags Delaney',
  tag: 'JOB 06 · THE LEDGER',
  mood: 'night',
  blurb:
    'Clive Vane keeps a ledger that proves DS Holloway takes the Vanes’ money. Get a revolver from Sid Marlowe at Gorringe’s Scrap Metals, take the ledger from the strongbox in the boot of Clive’s black Regent outside The Velvet Lamp, and bring it to Mags at St. Brannoc’s Church. Pays £4,000, more if you never fire a shot.',
  reward: 4000,
  requires: [THE_COLLECTION],
  requiresUnlock: UNLOCK_MAGS_FIRM,
  giver: { poi: 'mission-start-anchor', prompt: 'Knock on Mags’ back-room door', glyph: 'M' },
  introFocus: { poi: 'club-velvet-lamp' },
  intro: [
    { who: 'Mags', text: 'Holloway’s been on the Vanes’ books since decimal was a rumour. Clive writes it all down.' },
    { who: 'Mags', text: 'The ledger’s in a strongbox in the boot of his black Regent, outside the Lamp. Tonight.' },
    { who: 'Mags', text: 'Sid’s got a .38 for you. For show, love. Bring the ledger to St. Brannoc’s — I’ll be in the vestry.' },
  ],
  setup: (m) => {
    m.spawnVehicle('clive', 'saloon', { poi: 'club-velvet-lamp', offset: 6 }, { locked: true, paint: CLIVE_PAINT, parked: true });
  },
  onAttempt: (m) => {
    m.listen('weaponFired', () => (m.vars.fired = true), 'attempt');
  },
  protect: [{ vehicle: 'clive', minHealth: 0.01, reason: LEDGER_LOST }],
  stages: [
    {
      id: 'sid',
      kind: 'goTo',
      at: { poi: 'scrapyard-gorringe' },
      radius: 9,
      text: 'Collect a revolver from Sid Marlowe at Gorringe’s Scrap Metals, Cinder Yards.',
      hint: 'Sid is the revolver on the map. Just go to the yard.',
      onComplete: (m) => {
        // A replay is no free box of rounds: Sid only hands one over if you've no .38 on you.
        const armed = !!m.ctx.services.tryGet('weapons')?.owned().includes('revolver');
        if (m.replay && armed) m.toast('Sid checks you’ve still got the .38. No more rounds on the house.', 'info');
        else {
          m.giveWeapon('revolver', 12);
          m.toast('Sid hands you a .38 revolver and a dozen rounds.', 'good');
        }
        m.say([
          { who: 'Sid Marlowe', text: 'One .38, a dozen rounds, and you never saw me.' },
          { who: 'Sid Marlowe', text: 'Mags says it’s for show. So show it, son. Don’t use it.' },
        ]);
      },
    },
    {
      id: 'lamp',
      kind: 'goTo',
      at: { vehicle: 'clive' },
      radius: 14,
      text: 'Go to The Velvet Lamp and find Clive Vane’s black Regent parked outside.',
      hint: 'Park your car round the corner — the next part is done on foot.',
      // The reveal: the Regent under the club's lights, nobody watching yet.
      onComplete: (m) => m.glance({ vehicle: 'clive' }),
    },
    {
      id: 'box',
      kind: 'hold',
      at: { vehicle: 'clive' },
      radius: 4.5,
      seconds: STRONGBOX_SECONDS,
      prompt: 'Force open the strongbox',
      text: 'Open the strongbox in the black Regent’s boot: force it quietly, or shoot the lock.',
      hint: (touch) => `Quiet: ${touch ? 'hold USE' : 'hold E'} by the boot for 8 seconds. Loud: one shot opens it, but brings the police and loses the bonus.`,
      checkpoint: {
        label: 'at Clive’s Regent',
        restore: (m) => {
          const v = m.vehicle('clive');
          const kerb = m.resolve({ poi: 'club-velvet-lamp', offset: 6 });
          if (!v || v.destroyed || distance(v.pos, kerb) > 40) {
            m.spawnVehicle('clive', 'saloon', { poi: 'club-velvet-lamp', offset: 6 }, { locked: true, paint: CLIVE_PAINT, parked: true });
          }
          m.vars.doorman = false;
          m.vars.shotLock = false;
          m.setHeat(0);
          m.placePlayer({ poi: 'club-velvet-lamp', offset: 12 });
          rearm(m);
        },
      },
      onEnter: (m) => {
        m.listen('weaponFired', (e) => {
          const v = m.vehicle('clive');
          if (!v || distance(e.from, v.pos) > STRONGBOX_SHOT_RANGE || m.vars.shotLock) return;
          m.vars.shotLock = true;
          m.say([{ who: 'Doorman', text: 'Gunfire! Get down! Somebody get Mr Vane!' }]);
          m.advance();
        });
      },
      onProgress: (m, p) => {
        // Late enough that the law can't drive in from out of sight before the box is open: the
        // doorman is a scare and the reason the run starts hot, not an arrest mid-jemmy.
        if (p > DOORMAN_AT && !m.vars.doorman) {
          m.vars.doorman = true;
          m.say([{ who: 'Doorman', text: 'Here! What you doing in Mr Vane’s motor? Terry — get the law!' }]);
          raise(m, 1);
        }
      },
      onComplete: (m) => {
        m.vars.ledger = true;
        m.toast('You have Clive Vane’s ledger.', 'good');
        // The car's done its job: from here it's only a car.
        m.releaseVehicle('clive');
      },
    },
    {
      id: 'run',
      kind: 'escapeHeat',
      text: 'DS Holloway knows you have the ledger. Lose the police.',
      hint: 'Get out of their sight and stay hidden until your wanted level clears.',
      checkpoint: {
        label: 'ledger in hand',
        restore: (m) => {
          m.spawnVehicle('getaway', 'coupe', { poi: 'club-velvet-lamp', offset: 16 });
          m.placePlayer({ vehicle: 'getaway' }, 'getaway');
          m.releaseVehicle('getaway');
          m.setHeat(2);
          rearm(m);
        },
      },
      onEnter: (m) => {
        raise(m, 2);
        m.say([{ who: 'DS Holloway (radio)', text: 'All units, The Velvet Lamp. Suspect is carrying a book — evidence. Mine. I want it back unread.' }]);
        m.callPolice({ poi: 'club-velvet-lamp' }, { minDistance: 70 });
      },
    },
    {
      id: 'church',
      kind: 'goTo',
      at: { poi: 'st-brannocs' },
      radius: 10,
      requireHeat0: true,
      text: 'Take the ledger to Mags in the vestry at St. Brannoc’s Church.',
      blockedText: 'Holloway’s police are still after you. Lose them first, then go to the church.',
    },
  ],
  outroFocus: { poi: 'st-brannocs' },
  outro: [
    { who: 'Mags', text: 'Eleven years of Holloway’s pocket money, in Clive’s own handwriting. Lovely.' },
    { who: 'Mags', text: 'One copy for the Chief Constable, one for the Kessmouth Evening Post. Holloway can retire to the seaside.' },
    { who: 'Mags', text: 'And the Vanes? Without their copper they’re two loud men in good suits. Last orders, gents.' },
  ],
  payout: (m) => {
    const lines: RewardLine[] = [{ label: 'Mags’ thanks', amount: 4000 }];
    if (!m.vars.fired) lines.push({ label: 'Not a shot fired', amount: 1000 });
    return lines;
  },
};
